import type { DataPart, Part } from '@a2a-js/sdk';
import isObject from 'lodash/isObject';
import { nanoid } from 'nanoid';

import type {
  ChatMessage,
  ChatMessageArtifact,
  FollowUp,
  RechartsChartConfig,
} from '@cast/types';

const isDataPart = (p: Part): p is DataPart => p.kind === 'data';

const getDataType = (p: DataPart): string | undefined => {
  const type = p.data.type;
  return typeof type === 'string' ? type : undefined;
};

export const extractChartArtifacts = (parts: Part[]): ChatMessageArtifact[] =>
  parts
    .filter(isDataPart)
    .filter((p) => getDataType(p) === 'recharts_chart')
    .map((p) => ({
      type: 'recharts_chart' as const,
      chart: p.data as RechartsChartConfig,
    }));

const isFollowUp = (value: unknown): value is FollowUp => {
  if (!isObject(value)) {
    return false;
  }
  const command = (value as FollowUp).command;
  return typeof command === 'string' && !!command?.trim()?.length;
};

const extractFollowUps = (parts: Part[]): FollowUp[] | undefined => {
  return parts
    .filter(isDataPart)
    .flatMap((p) => p.data.follow_ups)
    .filter(isFollowUp);
};

export const mapPartsToMessages = (parts: Part[]): ChatMessage[] => {
  const nonChartParts = parts.filter(
    (p) =>
      !isDataPart(p) ||
      (getDataType(p) !== 'recharts_chart' && getDataType(p) !== 'plotly_chart')
  );
  const artifacts = extractChartArtifacts(parts);
  const followUps = extractFollowUps(parts);

  // Combine all text parts into a single message with artifacts attached
  const texts = nonChartParts
    .filter((p): p is Extract<Part, { kind: 'text' }> => p.kind === 'text')
    .map((p) => p.text);

  const combinedText = texts.join('\n\n');
  if (!combinedText && artifacts.length === 0) {
    return [];
  }

  return [
    {
      id: nanoid(),
      role: 'agent',
      text: combinedText,
      ...(artifacts.length > 0 ? { artifacts } : {}),
      ...(followUps && followUps.length > 0 ? { followUps } : {}),
    },
  ];
};
